import React, { useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { compose } from "@reduxjs/toolkit";
import * as yup from "yup";

import COMMON from "common";
import ERRORS from "common/errors";
import api from "services/api";
import pathnames from "routes/pathnames";
import withProfile from "contexts/with-profile";
import serveRequestErrors from "common/serve-request-errors";
import AppAlert from "components/app-alert";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppCheckbox from "components/app-checkbox";
import { getTotalDays } from "common/calendar";
import AppCalendarInput from "components/app-calendar-input";
import { CALENDAR_FORMAT } from "common/calendar";
import logo from "assets/images/logo.webp";
import AppMobileInput from "components/app-mobile-input";
import AppEmailExistModal from "components/pages/signup/app-email-exist-modal";
import AppOtpModal from "components/pages/signup/app-otp-modal";

const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const PageSignUp = (props) => {
    const emailExistErrorMsg = "there already exists an account registered with the email address";
    const emailExistRef = useRef();
    const otpModalRef = useRef();
    const navigate = useNavigate();
    const formik = useFormik({
        initialValues: {
            name: "",
            dateOfBirth: "",
            prefixNo: "",
            mobileNo: "",
            email: "",
            occupation: "",
            referralCode: "",
            consent: false,
        },
        validationSchema: yup.object({
            name: yup.string().required(ERRORS.REQUIRED),
            dateOfBirth: yup.string().required(ERRORS.REQUIRED).test("isAge18", ERRORS.BELOW_AGE_18, function (value) { 
                if (!value) return false;
                const days = getTotalDays(value, new Date());
                return days >= 6570; }),
            mobileNo: yup.string().required(ERRORS.REQUIRED),
            email: yup.string().matches(COMMON.REGEX.EMAIL, ERRORS.EMAIL).required(ERRORS.REQUIRED),
            occupation: yup.string().required(ERRORS.REQUIRED),
            consent: yup.boolean().oneOf([true], 'You must agree to the terms and conditions.'),
        }),
        onSubmit: (values) => {
            onHandleSendOtp(values);
        },
    });

    const onHandleSendOtp = async (values) => {
        try{
            console.log(values);
            await api.post.authenticate.sendOtp({ email: values.email });

            console.log("Response.success");
            formik.setSubmitting(false);
            onHandleOtpModal();

        } catch (error) {
            serveRequestErrors(error, (message) => {
                if (message.toLowerCase().includes(emailExistErrorMsg)) {
                    onHandleEmailExist();
                } else {
                    formik.setFieldError("referralCode", message);
                }
            });
        } finally {
            formik.setSubmitting(false);
        }
    }

    const onHandleSignUp = useCallback(async (otpCode) => {
        let values = { ...formik.values, otpCode };

        try {
            const payload = {
                email: values.email,
                fullName: values.name,
                mobileNoPrefix: values.prefixNo,
                mobileNo: values.mobileNo,
                occupation: values.occupation,
                referralCode: values.referralCode,
                dateOfBirth: values.dateOfBirth,
                otpCode: values.otpCode,
                consent: true,
            };

            await api.post.authenticate.signUp(payload);
            navigate(pathnames.createPassword, {
                state: {
                    email: values.email,
                    otpCode: values.otpCode,
                }
            });

        } catch (error) {
            serveRequestErrors(error, (message) => {
                if (message.toLowerCase().includes(emailExistErrorMsg)) {
                    onHandleEmailExist();
                } else {
                    formik.setFieldError("referralCode", message);
                }
            });
        } finally {
            formik.setSubmitting(false);
        }
    }, [formik, navigate]);

    //prettier-ignore
    const onHandleConsent = useCallback((boolean) => {
        console.log(boolean);
        formik.setFieldValue("consent", boolean);
    }, [formik]);

    const onHandleEmailExist = useCallback(() => {
        emailExistRef.current.onHandleShow();
    }, []);
    
    const onHandleOtpModal = useCallback(() => {
        otpModalRef.current.onHandleShow();
    }, []);

    return (
        <div className="page-signup">
            <div className="signup">
                <AppAlert />

                <main className="main">
                    <div className="main__content main__content--background">
                        <Link to={pathnames.login} className="main__logo">
                            <img src={logo} alt="logo" />
                        </Link>
                    </div>

                    <div className="main__content main__content--form">
                        <form className="main__form" onSubmit={formik.handleSubmit}>
                            <h1 className="main__title">Sign Up</h1>
                            <p className="main__description">Please fill in the required informations.</p>
                            <p className="main__description">Customer age must be 18 and above.</p>

                            {/* prettier-ignore */}
                            <AppInput required type="text" name="name" label="Name as per NRIC/Passport" placeholder="Enter your name" value={formik.values.name} error={formik.errors.name} touched={formik.touched.name} onChange={formik.handleChange} />

                            <AppCalendarInput required name="dateOfBirth" label="Date of Birth" placeholder="01/01/2023" displayFormat={calendarDisplayFormat} value={formik.values.dateOfBirth} error={formik.errors.dateOfBirth} touched={formik.touched.dateOfBirth} onChange={formik.setFieldValue} />

                            <AppMobileInput required name="mobileNo" label="Mobile No" placeholder="" value={formik.values.mobileNo} prefixNo={formik.values.prefixNo} error={formik.errors.mobileNo} touched={formik.touched.mobileNo} onChange={formik.handleChange} onChangeCode={formik.setFieldValue} disabled={formik.isSubmitting} />

                            <AppInput required type="text" name="email" label="Personal Email Address" placeholder="e.g. abc@gmail.com" value={formik.values.email} error={formik.errors.email} touched={formik.touched.email} onChange={formik.handleChange} />

                            <AppInput required type="text" name="occupation" label="Occupation" placeholder="e.g. marketing excutive" value={formik.values.occupation} error={formik.errors.occupation} touched={formik.touched.occupation} onChange={formik.handleChange} />

                            <AppInput type="text" name="referralCode" label="Referral Code" placeholder="e.g. ABCD01" value={formik.values.referralCode} error={formik.errors.referralCode} touched={formik.touched.referralCode} onChange={formik.handleChange} />

                            <div className="main__wrapper">
                                <AppCheckbox onClick={() => onHandleConsent(!formik.values.consent)} name="consent" label={
                                    <span className="main__wrapper__text-left">
                                        By clicking on this, I consent with{" "}
                                        <a
                                            href="/personal-data-privacy"
                                        >
                                            Personal Data Privacy Act
                                        </a>{" "}
                                        &{" "}
                                        <a
                                            href="/terms-and-conditions"
                                        >
                                            Terms & Condition
                                        </a>.
                                    </span>} value={formik.values.consent} disabled={formik.isSubmitting} />
                            </div>                            

                            <div className="main__button-container">
                                <AppButton type="submit" label="Next" disabled={formik.isSubmitting} />
                            </div>
                        </form>

                        <p className="main__copyright">© {new Date().getFullYear()} Altecflex.my. All Rights Reserved.</p>
                    </div>
                </main>
            </div>

            <AppOtpModal email={formik.values.email} ref={otpModalRef} onSubmit={onHandleSignUp} />
            <AppEmailExistModal ref={emailExistRef} />
        </div>
    );
};

export default compose(withProfile)(PageSignUp);
